import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Download, Loader2, Trash2, Database, RefreshCw, CheckCircle2, AlertCircle, Clock } from 'lucide-react';

const POLL_INTERVAL_MS = 4000;
const TERMINAL = new Set(['ready', 'failed', 'expired', 'deleted']);

function formatBytes(n) {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatRel(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = new Date();
    const ms = now - d;
    if (ms < 60_000) return 'just now';
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function StatusBadge({ status }) {
  const map = {
    queued:  { label: 'Queued',   cls: 'bg-slate-500/15 text-slate-300 border-slate-500/20',   icon: Clock },
    running: { label: 'Building', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/20',   icon: Loader2 },
    ready:   { label: 'Ready',    cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20', icon: CheckCircle2 },
    failed:  { label: 'Failed',   cls: 'bg-red-500/15 text-red-300 border-red-500/20',         icon: AlertCircle },
    expired: { label: 'Expired',  cls: 'bg-slate-700/30 text-slate-400 border-slate-600/30',   icon: AlertCircle },
    deleted: { label: 'Deleted',  cls: 'bg-slate-700/30 text-slate-400 border-slate-600/30',   icon: Trash2 },
  };
  const s = map[status] || { label: status || 'unknown', cls: 'bg-slate-500/15 text-slate-300', icon: Clock };
  const Icon = s.icon;
  return (
    <Badge className={`text-[9px] border ${s.cls} flex items-center gap-1`}>
      <Icon className={`w-2.5 h-2.5 ${status === 'running' ? 'animate-spin' : ''}`} />
      {s.label}
    </Badge>
  );
}

export default function TenantDataExport() {
  const [exports, setExports] = useState([]);
  const [meta, setMeta] = useState({ ttl_days: 7, daily_limit: 5 });
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await api.listTenantExports();
      setExports(data.items || []);
      setMeta({ ttl_days: data.ttl_days, daily_limit: data.daily_limit });
    } catch (e) {
      toast.error(e.message || 'Failed to load exports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-poll while any export is in-flight. The backend job is fast
  // (~1s for a typical tenant) so a 4-second poll is plenty.
  useEffect(() => {
    const inFlight = exports.some(e => !TERMINAL.has(e.status));
    if (!inFlight) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [exports, load]);

  const handleRequest = async () => {
    setRequesting(true);
    try {
      const rec = await api.requestTenantExport();
      toast.success('Export queued — your archive will be ready shortly');
      setExports(prev => [rec, ...prev]);
    } catch (e) {
      toast.error(e.message || 'Failed to request export');
    } finally {
      setRequesting(false);
    }
  };

  const handleDownload = (rec) => {
    // FileResponse uses cookie auth; just navigate the browser to the URL.
    window.location.href = api.tenantExportDownloadUrl(rec.export_id);
  };

  const handleDelete = async (rec) => {
    if (!window.confirm(`Delete this export? The downloadable archive will be removed immediately.`)) return;
    try {
      await api.deleteTenantExport(rec.export_id);
      toast.success('Export deleted');
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-4" data-testid="tenant-data-export">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Database className="w-5 h-5 text-primary" strokeWidth={1.5} />
          <div>
            <h2 className="text-base font-heading font-semibold text-foreground">Data Export</h2>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Download a complete archive of your tenant's data — for portability, backup, or off-boarding.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={loading}
            className="h-8 px-2 text-muted-foreground hover:text-foreground"
            data-testid="export-refresh-btn"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            size="sm"
            onClick={handleRequest}
            disabled={requesting}
            className="h-8"
            data-testid="export-request-btn"
          >
            {requesting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Database className="w-3.5 h-3.5 mr-1.5" />}
            Request export
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 flex items-start gap-2">
        <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-foreground/75 leading-relaxed">
          Archives bundle every tenant-scoped collection as JSON inside one ZIP, with a <span className="font-mono">manifest.json</span> listing record counts. Secrets and credentials are automatically redacted. Files expire after{' '}
          <span className="font-semibold text-foreground">{meta.ttl_days} days</span>; up to{' '}
          <span className="font-semibold text-foreground">{meta.daily_limit}</span> exports per day.
        </p>
      </div>

      {exports.length === 0 && !loading && (
        <div className="rounded-lg border border-dashed border-border/60 px-4 py-8 text-center">
          <Database className="w-6 h-6 text-muted-foreground/70 mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">No exports yet — click <span className="font-medium text-foreground">Request export</span> to create one.</p>
        </div>
      )}

      {exports.length > 0 && (
        <div className="rounded-lg border border-border/40 overflow-hidden" data-testid="export-history">
          <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-3 px-4 py-2 bg-muted/20 border-b border-border/30 text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">
            <div>Export</div>
            <div className="text-right">Records</div>
            <div className="text-right">Size</div>
            <div className="text-right">Status</div>
            <div className="text-right w-32">Actions</div>
          </div>
          {exports.map(rec => (
            <div key={rec.export_id} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-3 px-4 py-3 border-b border-border/20 last:border-0 items-center hover:bg-muted/10 transition-colors" data-testid={`export-row-${rec.export_id}`}>
              <div className="min-w-0">
                <p className="text-xs font-mono text-foreground truncate">{rec.export_id}</p>
                <p className="text-[10px] text-muted-foreground">
                  Requested {formatRel(rec.requested_at)}
                  {rec.status === 'ready' && rec.completed_at && (
                    <> · ready in {Math.max(1, Math.round((new Date(rec.completed_at) - new Date(rec.started_at || rec.requested_at)) / 1000))}s</>
                  )}
                  {rec.download_count > 0 && <> · downloaded {rec.download_count}×</>}
                  {rec.error && <span className="text-red-400"> · {rec.error}</span>}
                </p>
              </div>
              <div className="text-xs text-foreground text-right tabular-nums">{rec.records_count ?? '—'}</div>
              <div className="text-xs text-foreground text-right tabular-nums">{formatBytes(rec.file_size_bytes)}</div>
              <div className="text-right"><StatusBadge status={rec.status} /></div>
              <div className="flex items-center justify-end gap-1 w-32">
                {rec.status === 'ready' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-primary hover:bg-primary/10"
                    onClick={() => handleDownload(rec)}
                    data-testid={`export-download-${rec.export_id}`}
                  >
                    <Download className="w-3.5 h-3.5 mr-1" /> Download
                  </Button>
                )}
                {(rec.status === 'ready' || rec.status === 'failed') && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => handleDelete(rec)}
                    data-testid={`export-delete-${rec.export_id}`}
                    aria-label="Delete export"
                    title="Delete export"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
